import { useTranslation } from 'react-i18next'
import { ITEM_STATES, ItemAttributes, JobItem, JobProductItem } from '../../../../domain/items'
import styled from 'styled-components'
import { Button, Card, Col, Popover, Row, Tooltip } from 'antd'
import Icons from '../../../../../../../Main/Components/Icons'
import {
  CheckOutlined,
  CloseOutlined,
  CopyOutlined,
  DownOutlined,
  MessageOutlined,
  UpOutlined
} from '@ant-design/icons'
import { JobItemCardActions } from './job-item-card-actions.component'
import { useEffect } from 'react'
import { useHookstate } from '@hookstate/core'
import { formatMoney } from 'accounting-js'
import TextArea from 'antd/lib/input/TextArea'

const ItemJobCardSection = styled.div`
  & > div {
    margin-bottom: 1.8rem;
    border: none;
    border-top: 1px solid #f0f2f5;
    border-bottom: 1px solid #f0f2f5;
    border-radius: 0;
    & > div {
      padding: 0;
    }
  }
`

const HeaderCardSection = styled.div`
  z-index: 99999;
  position: relative;

  & > div {
    & > div:nth-child(1) {
      & > div {
        width: 100%;
        text-align: center;
        & > div {
          background: #b4e6fd;
          height: 3.2rem;
          width: 3.2rem;
          border-radius: 0 0 60% 0;
          float: left;
          & > svg {
            vertical-align: bottom;
          }
        }
      }
    }
  }
`

const ItemSection = styled.div`
  margin: -10px auto auto auto;
  padding-bottom: 1rem;
  & > div:nth-child(1) {
    & > div:nth-child(1) {
      & > div {
        text-align: center;
        & > img {
          box-shadow: 0 0 0.7rem 0.3rem #f4f4f4;
        }
      }
    }
    & > div:nth-child(2) {
      & > label:nth-child(1) {
        color: #000000d9;
        font-size: 1.4rem;
        font-style: normal;
        font-weight: 600;
        display: block;
      }
      & > label:nth-child(2) {
        color: #00000073;
        font-size: 1.4rem;
        font-style: normal;
        font-weight: 400;
        display: block;
      }
    }
    & > div:nth-child(3) {
      text-align: center;
    }
  }
  & > div:nth-child(2) {
    margin-bottom: 1.2rem;
    & > div:nth-child(2) {
      text-align: right;
      & label {
        color: #1677ff;
        font-size: 1.2rem;
        font-style: normal;
        font-weight: 500;
        cursor: pointer;
      }
    }
  }
`

const ItemDetailSection = styled.div`
  margin-bottom: 1rem;
  & > div {
    border: solid 0.1rem #eaedf3;
    border-radius: 0.8rem;
    & > div {
      padding: 0;
      & > div {
        padding: 0.5rem;
        & > div:nth-child(1) {
          text-align: left;
          & > label {
            text-transform: capitalize;
            color: #00000073;
            font-size: 1.2rem;
            font-style: normal;
            font-weight: 500;
          }
        }
        & > div:nth-child(2) {
          text-align: right;
          & > label {
            color: #00000073;
            font-size: 1.2rem;
            font-weight: 700;
            white-space: nowrap;
            width: 350px;
            overflow: hidden;
            text-overflow: ellipsis;
            display: inline-block;
          }
          & > span {
            color: #1677ff;
            font-size: 1.4rem;
            font-weight: 700;
            cursor: pointer;
            vertical-align: super;
            margin-left: 0.3rem;
          }
        }
      }
    }
  }
`

const ItemAttributeDetailSection = styled.div`
  text-align: center;
  background: #f8f9fb;
  display: block;
  padding: 0.5rem;
  margin: 1rem auto;
  & > label {
    color: #000000d9;
    font-size: 1.2rem;
    font-style: normal;
    font-weight: 700;
  }
`

const DivisionItemDetail = styled.div`
  width: 100%;
  border-top: solid 0.1rem #eaedf3;
  padding: 0 !important;
`

const SectionToShowComment = styled.div`
  & > div {
    cursor: pointer;
    color: #1677ff;
    font-size: 1.2rem;
    font-style: normal;
    font-weight: 500;
  }
  & > label {
    color: #00000073;
    font-size: 1.2rem;
    font-style: normal;
    font-weight: 600;
  }
`

const SectionToAddComment = styled.div`
  & > div {
    & > div {
      & > label {
        & > span:nth-child(1) {
          color: #1677ff !important;
          margin-right: 0.5rem;
        }
        & > span:nth-child(2) {
          color: #0000002b !important;
          margin-left: 0.5rem;
        }
        color: #00000073;
        font-size: 1.2rem;
        font-style: normal;
        font-weight: 600;
      }
    }
  }
`

type ItemJobCardProps = {
  item: JobItem
  currencyCode: string
  selectedItemsTab: string
}

const JobItemCard = (props: ItemJobCardProps) => {
  const { item, currencyCode, selectedItemsTab } = props
  const { t } = useTranslation()
  const jobProductItem = useHookstate<JobProductItem>({} as JobProductItem)
  const showItemAttibutesDetail = useHookstate<boolean>(false)
  const showSectionToAddComment = useHookstate<boolean>(false)

  useEffect(() => {
    createJobItemStructure(item)
  }, [])

  const createJobItemStructure = (item: JobItem): void => {
    const { quantity, presentationV2, photoUrl, foundQuantity, origin, itemId, state, attributes } = item

    const substitutedItems = []

    const price = ` ${t<string>('jobs.job.info.items-view.product-card.total-price')}: 
    ${formatMoney(presentationV2.price, currencyCode)}`
    const quantityText = ` ${state === ITEM_STATES.PENDING ? quantity : foundQuantity} ${t<string>(
      'jobs.job.info.items-view.product-card.from'
    )} ${presentationV2.quantity}`
    const unitPrice = ` ${t<string>('jobs.job.info.items-view.product-card.price')}: 
    ${formatMoney(presentationV2.price, currencyCode)}`

    const productItem: JobProductItem = {
      id: itemId,
      name: item.name,
      image: photoUrl,
      description: `${quantityText} ${presentationV2.unit} | ${price} ${currencyCode}`,
      quantity: item.quantity,
      quantityFound: item.foundQuantity,
      comment: item.comment,
      unitValue: `${unitPrice}`,
      unit: presentationV2.unit,
      addedBy: getWhoAddedTheItem(origin),
      actionType: '',
      attributes: {
        ean: attributes.ean,
        plu: attributes.plu,
        category: attributes.category,
        location: attributes.location,
        othersAttributes: attributes['...others'],
        pickingIndex: attributes.pickingIndex
      },
      replacementItem: substitutedItems
    }

    jobProductItem.set(productItem)
  }

  const getWhoAddedTheItem = (origin: string): string => {
    let actionType = origin

    return actionType
  }

  const renderSectionToShowComment = (): React.ReactElement => {
    return (
      <SectionToShowComment>
        {jobProductItem.get().comment && (
          <label>
            <MessageOutlined /> {jobProductItem.get().comment}
          </label>
        )}

        {!jobProductItem.get().comment && (
          <div
            onClick={() => {
              showSectionToAddComment.set(true)
            }}
          >
            <MessageOutlined /> {t<string>('addComment')}
          </div>
        )}
      </SectionToShowComment>
    )
  }

  const renderSectionToAddComment = (): React.ReactElement => {
    return (
      <SectionToAddComment>
        <Row>
          <Col span={24}>
            <label>
              <MessageOutlined />
              {t<string>('comment')}
              <span>({t<string>('optional')})</span>
            </label>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <Popover content={renderSectionToSaveComment()} visible placement='bottom'>
              <TextArea rows={2} placeholder={t<string>('writeCommentHere')}></TextArea>
            </Popover>
          </Col>
        </Row>
      </SectionToAddComment>
    )
  }

  const onCancelAddComment = () => {
    showSectionToAddComment.set(false)
  }
  const onSaveAddComment = () => {
    onCancelAddComment()
  }

  const renderSectionToSaveComment = (): React.ReactElement => {
    return (
      <Row gutter={16}>
        <Col span={12}>
          <Button type='default' size='small' onClick={onCancelAddComment}>
            <CloseOutlined />
          </Button>
        </Col>
        <Col span={12}>
          <Button type='default' size='small' onClick={onSaveAddComment}>
            <CheckOutlined />
          </Button>
        </Col>
      </Row>
    )
  }

  const renderItemAttribute = (options: JobProductItem | ItemAttributes, value: string): JSX.Element => {
    return (
      <Row>
        <Col span={6}>
          <label>{t<string>(value)}</label>
        </Col>
        <Col span={18}>
          <label>{options[value]}</label>
          <span>
            {value === 'ean' && (
              <Tooltip title={t<string>('copy')} placement='top' trigger='click'>
                <CopyOutlined
                  onClick={() => {
                    navigator.clipboard.writeText(options[value])
                  }}
                />
              </Tooltip>
            )}
          </span>
        </Col>
      </Row>
    )
  }

  return (
    <>
      {jobProductItem.get().id && (
        <ItemJobCardSection>
          <Card>
            <HeaderCardSection>
              <Row>
                <Col span={3}>
                  <div>
                    <div>
                      <Icons iconName='ReplacementByClientBlue' />
                    </div>
                  </div>
                </Col>
              </Row>
            </HeaderCardSection>
            <ItemSection>
              <Row>
                <Col span={4}>
                  <div>
                    <img
                      src={jobProductItem.get().image}
                      alt={jobProductItem.get().name}
                      style={{ height: '6.4rem', width: '6.4rem', borderRadius: '0.8rem' }}
                    />
                  </div>
                </Col>
                <Col span={14}>
                  <label>{jobProductItem.get().name}</label>
                  <label>{jobProductItem.get().description}</label>
                </Col>
                <Col span={6}>
                  <JobItemCardActions jobProductItem={jobProductItem} selectedItemsTab={selectedItemsTab} />
                </Col>
              </Row>

              <Row>
                <Col span={14} offset={4}>
                  {!showSectionToAddComment.get() && renderSectionToShowComment()}

                  {showSectionToAddComment.get() && renderSectionToAddComment()}
                </Col>
                <Col span={6}>
                  <label onClick={() => showItemAttibutesDetail.set(!showItemAttibutesDetail.get())}>
                    {t<string>('moreInfo')} {showItemAttibutesDetail.get() ? <UpOutlined /> : <DownOutlined />}
                  </label>
                </Col>
              </Row>

              {showItemAttibutesDetail.get() && (
                <Row>
                  <Col span={20} offset={4}>
                    <ItemDetailSection>
                      <Card>
                        {renderItemAttribute(JSON.parse(JSON.stringify(jobProductItem.get())), 'unitValue')}
                        <DivisionItemDetail></DivisionItemDetail>
                        {renderItemAttribute(JSON.parse(JSON.stringify(jobProductItem.get())), 'addedBy')}

                        <ItemAttributeDetailSection>
                          <label>{t<string>('itemDetails')}</label>
                        </ItemAttributeDetailSection>

                        {renderItemAttribute(JSON.parse(JSON.stringify(jobProductItem.get().attributes)), 'ean')}
                        <DivisionItemDetail></DivisionItemDetail>
                        {renderItemAttribute(JSON.parse(JSON.stringify(jobProductItem.get().attributes)), 'plu')}
                        <DivisionItemDetail></DivisionItemDetail>
                        {renderItemAttribute(JSON.parse(JSON.stringify(jobProductItem.get().attributes)), 'category')}

                        {jobProductItem.get().attributes.location && (
                          <>
                            <DivisionItemDetail></DivisionItemDetail>
                            {renderItemAttribute(
                              JSON.parse(JSON.stringify(jobProductItem.get().attributes)),
                              'location'
                            )}
                            <DivisionItemDetail></DivisionItemDetail>
                          </>
                        )}

                        {jobProductItem.get().attributes.othersAttributes && (
                          <>
                            {renderItemAttribute(
                              JSON.parse(JSON.stringify(jobProductItem.get().attributes)),
                              'othersAttributes'
                            )}
                            <DivisionItemDetail></DivisionItemDetail>
                          </>
                        )}

                        {jobProductItem.get().attributes.pickingIndex && (
                          <>
                            {renderItemAttribute(
                              JSON.parse(JSON.stringify(jobProductItem.get().attributes)),
                              'pickingIndex'
                            )}
                          </>
                        )}
                      </Card>
                    </ItemDetailSection>
                  </Col>
                </Row>
              )}
            </ItemSection>
          </Card>
        </ItemJobCardSection>
      )}
    </>
  )
}

export { JobItemCard }
